



























































import {
    PropType,
    computed,
    defineComponent,
} from '@vue/composition-api';
import {
    differenceInDays,
    differenceInHours,
    isTomorrow,
    translationBoldFormat,
    translationPlaceholderFormat,
} from '@superbet-group/web.lib.utilities';
import {
    isMobileLayout,
    useI18n,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import authStore from '@store/modules/data/authentication/authenticationIndex';
import {
    SdsBadge,
    SdsButton,
    SdsButtonText,
} from '@superbet-group/web.lib.ui-components';
import { BonusKind } from '@core/models/bonus/Bonus';

export default defineComponent({
    name: 'CardActionSection',
    emits: [
        'actionClick',
        'toggleDetails',
        'update:areDetailsVisible',
    ],
    props: {
        kind: {
            type: String as PropType<BonusKind>,
            required: true,
        },
        expirationDate: {
            type: Date,
            required: false,
            default: null,
        },
        isCTAButtonVisible: {
            type: Boolean,
            required: false,
            default: false,
        },
        isConditionSectionVisible: {
            type: Boolean,
            required: false,
            default: true,
        },
        buttonText: {
            type: String,
            required: false,
            default: '',
        },
        areDetailsVisible: {
            type: Boolean,
            required: false,
            default: false,
        },
        name: {
            type: String,
            required: false,
            default: '',
        },
        isBonusOnHold: {
            type: Boolean,
            required: false,
            default: false,
        },
        isLoading: {
            type: Boolean,
            required: false,
            default: false,
        },
        isCTAButtonDisabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        isWelcomeOfferPromotion: {
            type: Boolean,
            required: false,
            default: false,
        },
        isBetlerWelcomeOffer: {
            type: Boolean,
            required: false,
            default: false,
        },
        isExpirationDateHidden: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    // eslint-disable-next-line sonarjs/cognitive-complexity
    setup(props, { emit }) {
        const {
            translate,
            translatePluralized,
            formatDateTime,
        } = useI18n();

        const authModule = useVuexStore<typeof authStore>('data/authentication');
        const user = authModule.getter('user');

        const hoursDifference = props.expirationDate ? differenceInHours(props.expirationDate, new Date()) : 0;
        const expirationDateFormattedText = computed(() => {
            if (props.isExpirationDateHidden) {
                return null;
            } else if (!props.expirationDate) {
                return translate('bonus_comms.common.does_not_expire');
            }
            const dayDifference = differenceInDays(props.expirationDate, new Date());
            if (hoursDifference < 1) {
                return `<b>${translate('bonus_comms.common.less_than_hour')}</b>`;
            } else if (dayDifference < 1) {
                return translationBoldFormat(translationPlaceholderFormat(
                    translate('bonus_comms.common.expires_in'),
                    { time: `${hoursDifference} ${translatePluralized('pam.general.hoursPluralized', hoursDifference)}` },
                ));
            } else if (hoursDifference >= 24 && isTomorrow(props.expirationDate)) {
                return translate('bonus_comms.common.tomorrow');
            } else if (dayDifference <= 7) {
                return translationBoldFormat(translationPlaceholderFormat(
                    translate('bonus_comms.common.expires_in'),
                    { time: `${dayDifference} ${translatePluralized('pam.general.daysPluralized', dayDifference)}` },
                ));
            } else {
                return translationBoldFormat(translationPlaceholderFormat(
                    translate('bonus.card.expires_on'),
                    { condition: formatDateTime(props.expirationDate) },
                ));
            }
        });
        const chevronIconClass = computed(() => `${props.areDetailsVisible ? 'navigation-chevron-up' : 'navigation-chevron-down'}`);

        function toggleDetails() {
            emit('update:areDetailsVisible', !props.areDetailsVisible);
            emit('toggleDetails');
        }

        return {
            expirationDateFormattedText,
            chevronIconClass,
            isStaticWOTextVisible: computed(() => props.isWelcomeOfferPromotion && (!props.isBetlerWelcomeOffer || !user.value)),
            areConditionsHidden: computed(() => !props.isConditionSectionVisible),
            detailButtonSize: computed(() => isMobileLayout() ? 'sm' : 'md'),
            toggleDetails,
        };
    },
    components: {
        SdsButton,
        SdsButtonText,
        SdsBadge,
    },
});
